/* eslint-disable max-lines */
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { SHA256, enc } from 'crypto-js';
import {
  LoginAuthWithCredentials,
  LoginAuthWithCredentialsParams,
  OauthAEMConfig,
  PkceCode,
  PostWithCredentials
} from '../../models';
import { UtilMethodsService } from './util-methods.service';
import { UtilServiceHelperService } from './util-service-helper.service';

@Injectable({
  providedIn: 'root'
})
export class UtilService extends UtilServiceHelperService {
  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    protected override utilMethodsService: UtilMethodsService
  ) {
    super(utilMethodsService);
  }

  generateCodeChallenge(): PkceCode {
    const codeVerifier =
      this.utilMethodsService.generateStrRandomForCodeChallenge(128);
    const codeVerifierHash = SHA256(codeVerifier).toString(enc.Base64);
    const codeChallenge = codeVerifierHash
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    const code = { codeVerifier, codeChallenge };
    return code;
  }

  getPingServiceUrl(
    oauthConfig: OauthAEMConfig | undefined,
    code?: string,
    codeChallenge?: string
  ) {
    const pingEnv = oauthConfig?.PING_DOMAIN;
    const pingUrl = {
      loginOAuthUrl: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}`,
      loginOAuthUrlRememberMe: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE_RM}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      loginOAuthUrlPersistant: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.PROMPT}=${oauthConfig?.PROMPT_VALUE}`,
      loginOAuthUrlPersistantRememberMe: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}&${oauthConfig?.PROMPT}=${oauthConfig?.PROMPT_VALUE}`,
      mfaLoginOAuthUrl: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.MFA_ACTION}=${oauthConfig?.MFA_ACTION_VALUE}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      newUserMfaLoginOAuthUrlWithoutEmailValidation: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      newUserMfaLoginOAuthUrlEmailValidation: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.VERIFY_EMAIL_MFA}=${oauthConfig?.VERIFY_EMAIL_MFA_VALUE}`,
      selfServiceResetLoginOAuthUrl: `${pingEnv}${oauthConfig?.OAUTH2}${oauthConfig?.CLIENT_ID}=${oauthConfig?.MFA_CLIENT_ID_VALUE}&${oauthConfig?.RESPONSE_TYPE}=${oauthConfig?.RESPONSE_TYPE_VALUE}&${oauthConfig?.SCOPE}=${oauthConfig?.SCOPE_VALUE}&${oauthConfig?.RESPONSE_MODE}=${oauthConfig?.RESPONSE_MODE_VALUE}&${oauthConfig?.CODE_CHALLENGE_METHOD}=${oauthConfig?.CODE_CHALLENGE_METHOD_VALUE}&${oauthConfig?.CODE_CHALLENGE}=${codeChallenge}&${oauthConfig?.MFA_ACTION}=${oauthConfig?.MFA_ACTION_SSPR_VALUE}&${oauthConfig?.VERSION_PARAMETER}=${oauthConfig?.VERSION_PARAMETER_VALUE}`,
      loginAuthWithCredentialsUrl: `${pingEnv}${oauthConfig?.OAUTH2_USER_CREDENTIALS}${code}`,
      swapAuthForAccessTokensUrl: `${pingEnv}${oauthConfig?.OAUTH2_TOKEN}`,
      body: `${oauthConfig?.GRANT_TYPE}=${oauthConfig?.AUTHORIZATION_CODE}&${oauthConfig?.CODE}=${code}&${oauthConfig?.CODE_VERIFIER}=${codeChallenge}&${oauthConfig?.CLIENT_ID}=${oauthConfig?.CLIENT_ID_VALUE}`,
      loginOAuthRedirectfulUrl: `${pingEnv}${oauthConfig?.OAUTH2_USER_CREDENTIALS}${code}`,
      introspectionUrl: `${pingEnv}${oauthConfig?.INTROSPECTION}`
    };
    return pingUrl;
  }

  getLocalStorageValue(key: string) {
    let value = '';
    if (this.isLocalStorageKeyAvailable(key)) {
      value += localStorage.getItem(key);
    }
    return value;
  }

  clearLocalStorageValue(key: string) {
    let status = false;

    if (this.isLocalStorageKeyAvailable(key)) {
      localStorage.removeItem(key);
      status = true;
    }

    return status;
  }

  isLocalStorageKeyAvailable(key: string) {
    let result = false;

    if (this.isLocalStorageSupported() && localStorage.getItem(key)) {
      result = true;
    }

    return result;
  }

  async loginAuthWithCredResponse(
    loginAuthWithCredentialsParams: LoginAuthWithCredentialsParams,
    requestPayload: PostWithCredentials,
    apiHeaders: HttpHeaders
  ) {
    loginAuthWithCredentialsParams.flowId =
      loginAuthWithCredentialsParams.authId
        ? loginAuthWithCredentialsParams.authId
        : loginAuthWithCredentialsParams.flowId;

    const loginAuthWithCredentialsUrl = this.getPingServiceUrl(
      loginAuthWithCredentialsParams.oauthConfig,
      loginAuthWithCredentialsParams.flowId,
      ''
    ).loginAuthWithCredentialsUrl;

    if (isPlatformBrowser(this.platformId) && loginAuthWithCredentialsUrl) {
      const data: LoginAuthWithCredentials = await this.http
        .post<LoginAuthWithCredentials>(
          `${loginAuthWithCredentialsUrl}`,
          requestPayload,
          {
            headers: apiHeaders,
            responseType: 'json',
            withCredentials: true
          }
        )
        .toPromise()
        .catch((error) => {
          if (
            this.utilMethodsService.isChallengeScreen(
              loginAuthWithCredentialsParams,
              error
            )
          ) {
            const errorResponse = {
              errors: [{ code: 'ERROR_MSG_SEC_CP_CHALLENGE' }]
            };
            return errorResponse;
          } else if (
            this.utilMethodsService.isEamOutageCode(
              loginAuthWithCredentialsParams,
              error
            )
          ) {
            const errorResponse = {
              errors: [{ code: 'ERROR_MSG_128' }]
            };
            return errorResponse;
          }
          return error;
        });
      return data;
    } else {
      const errorResponse = {
        errors: [{ code: 'ERROR_MSG_222' }]
      };
      return errorResponse;
    }
  }
}
